import type {
  AppReduxState, AuthorizePageAction, ShowOverlayAction, UpdateConfigAction, UpdateImageQueuePatientStatusAction, UpdateMeAction,
  UpdatePageAction, UpdatePatientAction, UpdatePatientIdAction, UpdateSettingsAction, UpdateSystemSettingsAction, VerifyDisabledAction
} from './types';
import type { ReduxAction, ReduxReducer } from '../types.ts';
import { AppAction } from './enums.ts';

function showOverlay(state: AppReduxState, { show }: ShowOverlayAction): AppReduxState {
  return ({ ...state, showOverlay: show });
}

function updatePage(state: AppReduxState, { page, params, query, requiresAuth }: UpdatePageAction): AppReduxState {
  return ({ ...state, page, params, query, authReady: !requiresAuth, authGranted: requiresAuth ? undefined : true });
}

/** If page doesn't match the current page, it means they've navigated to a new page before we authed, so throw away this auth. */
function authorizedPage(state: AppReduxState, { page, granted }: AuthorizePageAction): AppReduxState {
  return page === state.page
    ? ({ ...state, authReady: true, authGranted: Boolean(granted) })
    : state;
}

function updateConfig(state: AppReduxState, { config }: UpdateConfigAction): AppReduxState {
  return ({ ...state, config });
}

function updateSettings(state: AppReduxState, { settings }: UpdateSettingsAction): AppReduxState {
  return ({ ...state, settings, settingsLoaded: true });
}

function updateImageQueuePatientStatus(state: AppReduxState, { imageQueuePatientStatus }: UpdateImageQueuePatientStatusAction): AppReduxState {
  return ({ ...state, imageQueuePatientStatus });
}

function updateMe(state: AppReduxState, { me }: UpdateMeAction): AppReduxState
{
  return ({ ...state, me });
}

function updatePatient(state: AppReduxState, { patient }: UpdatePatientAction): AppReduxState {
  return ({ ...state, patient });
}

function updatePatientId(state: AppReduxState, { patientId } : UpdatePatientIdAction): AppReduxState {
  return ({ ...state, patientId });
}

function updateSystemSettings(state: AppReduxState, { systemSettings }: UpdateSystemSettingsAction): AppReduxState {
  return ({ ...state, systemSettings });
}

function verifyDisabled(state: AppReduxState, { disabled }: VerifyDisabledAction): AppReduxState {
  return ({ ...state, verifyDisabled: disabled });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const reducers: Record<AppAction, ReduxReducer<AppReduxState, any>> = {
  [AppAction.UpdatePage]: updatePage,
  [AppAction.AuthorizePage]: authorizedPage,
  [AppAction.UpdateConfig]: updateConfig,
  [AppAction.UpdateSettings]: updateSettings,
  [AppAction.UpdateMe]: updateMe,
  [AppAction.UpdatePatient]: updatePatient,
  [AppAction.ShowOverlay]: showOverlay,
  [AppAction.VerifyDisabled]: verifyDisabled,
  [AppAction.UpdateSystemSettings]: updateSystemSettings,
  [AppAction.UpdateImageQueuePatientStatus]: updateImageQueuePatientStatus,
  [AppAction.UpdatePatientId]: updatePatientId
};

export function app(state: AppReduxState = {}, action: ReduxAction): AppReduxState {
  return (
    action.type in reducers
      ? reducers[action.type as AppAction]
      : (state: AppReduxState) => state
  )(state, action);
}
